import React, { useEffect, useMemo, useState } from "react";
import { logWarn } from "../lib/logger";
import { api, statusColor, statusLabel } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ShieldCheck, Users, BriefcaseBusiness, FileText, Archive, TrendingUp, UserPlus, Pencil, ChevronDown, UsersRound } from "lucide-react";

const STATUS_ORDER = ["saved", "applied", "interviewing", "offer", "rejected"];
const WINDOWS = [
  { v: "7", label: "Last 7 days" },
  { v: "30", label: "Last 30 days" },
  { v: "60", label: "Last 60 days" },
  { v: "90", label: "Last 90 days" },
];
const JOB_SERIES = [
  { v: "created", label: "Created" },
  ...STATUS_ORDER.map((s) => ({ v: s, label: statusLabel(s) })),
];

const DATE_FMT = { month: "short", day: "numeric" };
const CHART_MARGIN = { top: 4, right: 8, left: -20, bottom: 4 };
const TOOLTIP_CONTENT_STYLE = { border: "2px solid #18181b", borderRadius: 0, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { fontWeight: 700 };
const AXIS_TICK = { fontSize: 10 };
const CHART_BOX = { width: "100%", height: 240 };

const withLabels = (series) =>
  (series || []).map((p) => ({ ...p, label: new Date(p.date + "T00:00:00").toLocaleDateString(undefined, DATE_FMT) }));

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [state, setState] = useState("loading");
  const [windowDays, setWindowDays] = useState("30");
  const [trends, setTrends] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [jobSeries, setJobSeries] = useState("created");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/stats");
        setStats(r.data);
        setState("ready");
      } catch (err) {
        setState(err.response?.status === 403 ? "forbidden" : "error");
      }
    })();
  }, []);

  useEffect(() => {
    if (state !== "ready") return;
    setTrendsLoading(true);
    api.get(`/admin/trends?window_days=${windowDays}`)
      .then((r) => setTrends(r.data))
      .catch((e) => logWarn("trends load failed", e))
      .finally(() => setTrendsLoading(false));
  }, [state, windowDays]);

  const signupData = useMemo(() => withLabels(trends?.signups), [trends]);
  const activeData = useMemo(() => withLabels(trends?.active_users), [trends]);
  const jobData = useMemo(() => withLabels(trends?.jobs?.[jobSeries]), [trends, jobSeries]);
  const jobColor = jobSeries === "created" ? "#18181b" : statusColor(jobSeries);

  if (state === "loading") return <div className="label-tiny" data-testid="admin-loading">Loading admin stats…</div>;
  if (state === "forbidden")
    return (
      <div className="brutal-card p-8 text-center max-w-md mx-auto" data-testid="admin-forbidden">
        <ShieldCheck size={28} className="mx-auto text-zinc-400" />
        <h1 className="font-heading text-2xl font-black mt-3">Owner only</h1>
        <p className="text-zinc-600 mt-2">You don't have admin access to this page.</p>
      </div>
    );
  if (state === "error") return <div className="label-tiny text-red-600" data-testid="admin-error">Couldn't load admin stats.</div>;

  const cards = [
    { label: "Total users", value: stats.total_users, icon: Users, tid: "admin-total-users" },
    { label: "Total jobs", value: stats.total_jobs, icon: BriefcaseBusiness, tid: "admin-total-jobs" },
    { label: "Active jobs", value: stats.active_jobs, icon: BriefcaseBusiness, tid: "admin-active-jobs" },
    { label: "Archived jobs", value: stats.archived_jobs, icon: Archive, tid: "admin-archived-jobs" },
    { label: "Resumes", value: stats.resumes, icon: FileText, tid: "admin-resumes" },
  ];

  const kpis = trends?.kpis;

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-page">
      <div className="label-tiny">Owner</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Admin stats</h1>
      <p className="text-zinc-600 mt-2">Live production counts across all accounts.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
        {cards.map((c) => (
          <div key={c.tid} className="brutal-card p-5" data-testid={c.tid}>
            <c.icon size={16} strokeWidth={1.5} className="text-zinc-400" />
            <div className="font-heading text-3xl font-black mt-3 tabular-nums">{c.value}</div>
            <div className="label-tiny mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="brutal-card p-6 mt-6" data-testid="admin-jobs-by-status">
        <div className="label-tiny mb-4">Jobs by status</div>
        <div className="border border-zinc-200">
          {STATUS_ORDER.map((s) => (
            <div key={s} className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 last:border-b-0" data-testid={`admin-status-${s}`}>
              <span className="text-sm capitalize">{s}</span>
              <span className="font-mono-jb text-sm tabular-nums">{stats.jobs_by_status?.[s] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- TRENDS ---------- */}
      <div className="flex items-end justify-between flex-wrap gap-4 mt-12">
        <div>
          <div className="label-tiny flex items-center gap-2"><TrendingUp size={13} /> Growth</div>
          <h2 className="font-heading text-2xl font-black tracking-tight mt-2">Trends</h2>
        </div>
        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="rounded-none h-10 w-44 border-zinc-300" data-testid="trends-window-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.v} value={w.v} data-testid={`trends-window-${w.v}`}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards — always this week (last 7 days) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" data-testid="admin-kpis">
        <Kpi icon={UserPlus} label="New users this week" value={kpis?.new_users_this_week} tid="kpi-new-users" />
        <Kpi icon={BriefcaseBusiness} label="Jobs added this week" value={kpis?.jobs_added_this_week} tid="kpi-jobs-added" />
        <Kpi icon={Pencil} label="Jobs edited this week" value={kpis?.jobs_edited_this_week} tid="kpi-jobs-edited" />
      </div>

      {trendsLoading ? (
        <div className="space-y-3 mt-6">{["a", "b", "c"].map((k) => <div key={k} className="h-56 bg-zinc-100" />)}</div>
      ) : !trends ? (
        <div className="brutal-card p-8 text-center mt-6"><div className="label-tiny">Could not load trends</div></div>
      ) : (
        <div className="space-y-6 mt-6">
          <TrendChart title="Signups over time" testid="chart-signups" data={signupData} color="#002FA7" gradId="gradSignups" />

          <section className="brutal-card p-6" data-testid="chart-jobs">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <div className="label-tiny flex items-center gap-2"><BriefcaseBusiness size={13} /> Jobs over time</div>
              <Select value={jobSeries} onValueChange={setJobSeries}>
                <SelectTrigger className="rounded-none h-9 w-40 border-zinc-300" data-testid="trends-jobseries-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_SERIES.map((o) => (
                    <SelectItem key={o.v} value={o.v} data-testid={`trends-jobseries-${o.v}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Area5 data={jobData} color={jobColor} gradId="gradJobs" />
          </section>

          <ActiveUsersSection data={activeData} />
        </div>
      )}
    </div>
  );
}

const ActiveUsersSection = ({ data }) => {
  const dates = useMemo(() => data.map((d) => d.date), [data]);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const pageSize = 20;

  // Keep the selected day valid within the current window (default = latest day).
  useEffect(() => {
    if (dates.length && !dates.includes(date)) {
      setDate(dates[dates.length - 1]);
      setPage(1);
    }
  }, [dates, date]);

  // Lazy-load: only fetch when the section is open. `ignore` guards against a
  // slow earlier response overwriting newer state on rapid date/page switches.
  useEffect(() => {
    if (!open || !date) return;
    let ignore = false;
    setLoading(true);
    setError(false);
    api.get(`/admin/active-users?date=${date}&page=${page}&page_size=${pageSize}`)
      .then((r) => { if (!ignore) { setRows(r.data.users); setTotal(r.data.total); } })
      .catch((e) => { logWarn("active-users load failed", e); if (!ignore) { setError(true); setRows([]); setTotal(0); } })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [open, date, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <section className="brutal-card p-6" data-testid="chart-active-users">
      <div className="label-tiny flex items-center gap-2 mb-5"><TrendingUp size={13} /> Active users</div>
      <Area5 data={data} color="#10B981" gradId="gradActive" />

      <Collapsible open={open} onOpenChange={setOpen} className="mt-4 border-t border-zinc-200 pt-4">
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full text-left group" data-testid="active-users-detail-toggle">
            <span className="label-tiny flex items-center gap-2"><UsersRound size={13} /> Who was active</span>
            <ChevronDown size={16} className={"transition-transform " + (open ? "rotate-180" : "")} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4" data-testid="active-users-detail">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <Select value={date} onValueChange={(v) => { setDate(v); setPage(1); }}>
              <SelectTrigger className="rounded-none h-9 w-48 border-zinc-300" data-testid="active-users-date-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...dates].reverse().map((d) => (
                  <SelectItem key={d} value={d} data-testid={`active-users-date-${d}`}>{fmt(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="label-tiny" data-testid="active-users-total">{total} active</span>
          </div>

          {loading ? (
            <div className="h-24 bg-zinc-100" data-testid="active-users-loading" />
          ) : error ? (
            <div className="text-sm text-red-600 py-6 text-center" data-testid="active-users-error">Couldn't load active users. Try again.</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="active-users-empty">No accounts were active on this day.</div>
          ) : (
            <>
              <Table data-testid="active-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Jobs added</TableHead>
                    <TableHead className="text-right">Status changes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((u) => (
                    <TableRow key={u.user_id} data-testid={`active-user-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.name || u.email}</span>
                          {u.signed_up && <Badge className="rounded-none bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`active-user-new-${u.user_id}`}>New</Badge>}
                        </div>
                        {u.name ? <div className="text-xs text-zinc-500">{u.email}</div> : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{u.jobs_added}</TableCell>
                      <TableCell className="text-right tabular-nums">{u.status_changes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {total > pageSize && (
                <div className="flex items-center justify-between mt-3">
                  <Button variant="outline" className="rounded-none border-zinc-300 h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="active-users-prev">Prev</Button>
                  <span className="label-tiny" data-testid="active-users-page">Page {page} of {totalPages}</span>
                  <Button variant="outline" className="rounded-none border-zinc-300 h-8" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="active-users-next">Next</Button>
                </div>
              )}
            </>
          )}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};

const Kpi = ({ icon: Icon, label, value, tid }) => (
  <div className="brutal-card p-5" data-testid={tid}>
    <Icon size={16} strokeWidth={1.5} className="text-zinc-400" />
    <div className="font-heading text-3xl font-black mt-3 tabular-nums">{value ?? "—"}</div>
    <div className="label-tiny mt-1">{label}</div>
  </div>
);

const Area5 = ({ data, color, gradId }) => (
  <div style={CHART_BOX}>
    <ResponsiveContainer>
      <AreaChart data={data} margin={CHART_MARGIN}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
        <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" minTickGap={28} stroke="#a1a1aa" />
        <YAxis allowDecimals={false} tick={AXIS_TICK} stroke="#a1a1aa" />
        <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
        <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2} fill={`url(#${gradId})`} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const TrendChart = ({ title, testid, data, color, gradId }) => (
  <section className="brutal-card p-6" data-testid={testid}>
    <div className="label-tiny flex items-center gap-2 mb-5"><TrendingUp size={13} /> {title}</div>
    <Area5 data={data} color={color} gradId={gradId} />
  </section>
);
